/**
 * The five-state matrix (ux.md §12).
 *
 * Every list and detail surface ships all five: empty, loading, partial, error, forbidden. They are
 * components rather than a convention because a convention is what gets skipped on the screen
 * nobody demoed.
 *
 * FORBIDDEN is the one people leave out, and it is the one that matters most here. A member without
 * a permission is not an error and not an empty list — showing them either is a lie about the
 * system's state, and "there are no members" when there are forty is worse than a refusal. It is
 * also the state most likely to be reached by someone whose access was just narrowed, so it says
 * what to do next.
 *
 * PARTIAL is the second most-skipped. A list that loaded eight of ten pages is not "loaded" and not
 * "failed"; hiding the gap makes an incomplete answer look complete, which for an audit trail or a
 * member list is a correctness problem rather than a polish one.
 *
 * The DEAD-END BAN (ux.md §12) applies to all of them: every state offers a way forward. `action`
 * is optional in the type only because some callers legitimately have nothing to offer; where a
 * retry or a request-access path exists, it belongs here.
 */
import type { ReactNode } from 'react'

export type StateAction = {
  readonly label: string
  readonly onAction: () => void
}

type ShellProps = {
  readonly tone?: 'neutral' | 'danger'
  readonly title: string
  readonly body?: ReactNode
  readonly action?: StateAction
  /** ARIA role. `alert` for errors and forbidden; `status` for the calmer states. */
  readonly role?: 'status' | 'alert'
}

function StateShell({ tone = 'neutral', title, body, action, role = 'status' }: ShellProps) {
  return (
    <div
      role={role}
      // `polite` even for alerts: an error appearing while someone is mid-keystroke should not
      // interrupt them, and every one of these states is also visible on screen.
      aria-live="polite"
      className="border-border-subtle bg-surface-sunken flex flex-col items-start gap-2 rounded-md border px-4 py-6"
    >
      <p className={`m-0 font-medium ${tone === 'danger' ? 'text-danger' : 'text-text-primary'}`}>
        {title}
      </p>
      {body ? <div className="text-text-muted m-0 max-w-prose">{body}</div> : null}
      {action ? (
        <button
          type="button"
          onClick={action.onAction}
          className="h-control border-border-strong bg-surface-raised text-text-primary mt-1 rounded-sm border px-3"
        >
          {action.label}
        </button>
      ) : null}
    </div>
  )
}

export type EmptyStateProps = {
  readonly title: string
  readonly body?: ReactNode
  readonly action?: StateAction
}

export function EmptyState(props: EmptyStateProps) {
  return <StateShell {...props} />
}

export type LoadingStateProps = {
  /** Names what is loading. "Loading…" alone tells a screen-reader user nothing useful. */
  readonly label: string
}

export function LoadingState({ label }: LoadingStateProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className="border-border-subtle bg-surface-sunken flex flex-col gap-2 rounded-md border px-4 py-6"
    >
      <span className="visually-hidden">{label}</span>
      {/* Skeleton rows at the real row height, so the layout does not jump when data lands. */}
      {[0, 1, 2].map((i) => (
        <div key={i} aria-hidden="true" className="h-row bg-surface-raised w-full rounded-sm" />
      ))}
    </div>
  )
}

export type PartialStateProps = {
  readonly loaded: number
  readonly total: number
  readonly onRetry?: () => void
}

export function PartialState({ loaded, total, onRetry }: PartialStateProps) {
  return (
    <StateShell
      title={`Showing ${loaded} of ${total}`}
      body="Some records could not be loaded. What you see is incomplete."
      {...(onRetry ? { action: { label: 'Load the rest', onAction: onRetry } } : {})}
    />
  )
}

export type ErrorStateProps = {
  readonly title?: string
  readonly body?: ReactNode
  readonly onRetry?: () => void
  /**
   * A way forward for a failure that RETRYING CANNOT CLEAR, offered instead of "Try again".
   *
   * A 404 is the case this exists for: the route is not deployed, or the row is not one this
   * account can open. Either way the next attempt answers exactly the same, so a Try again button
   * is an invitation to press something that can never work — the dead end `ux.md` §12 bans,
   * wearing the costume of a remedy. `Connect.tsx` makes the same call by rendering its
   * not-deployed case as a state of its own; this is that reasoning for the screens where the
   * failure is still an error but the button has to go somewhere else.
   *
   * Takes precedence over `onRetry`, so a caller that has both states passes whichever fits the
   * error in hand rather than drawing two buttons.
   */
  readonly action?: StateAction
}

export function ErrorState({
  title = 'Something went wrong',
  body,
  onRetry,
  action,
}: ErrorStateProps) {
  return (
    <StateShell
      tone="danger"
      role="alert"
      title={title}
      body={body ?? 'This did not load. Nothing was changed.'}
      {...(action
        ? { action }
        : onRetry
          ? { action: { label: 'Try again', onAction: onRetry } }
          : {})}
    />
  )
}

export type ForbiddenStateProps = {
  /** What they were trying to see, in the user's words. */
  readonly resource: string
  readonly onRequestAccess?: () => void
}

export function ForbiddenState({ resource, onRequestAccess }: ForbiddenStateProps) {
  return (
    <StateShell
      role="alert"
      title={`You do not have access to ${resource}`}
      // Names the mechanism, because "contact your administrator" without saying why reads as a
      // bug. Someone whose access was just narrowed needs to know that it changed, not guess.
      body="Your role in this organization does not include this. An organization admin can change it."
      {...(onRequestAccess
        ? { action: { label: 'Request access', onAction: onRequestAccess } }
        : {})}
    />
  )
}
